import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { getMonthlyDetail, downloadPdfReport, formatCurrency, MONTHS, TYPE_COLORS } from '../lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft, ChevronLeft, ChevronRight, FileText, TrendingUp, TrendingDown,
  PiggyBank, CreditCard, Sparkles, Award, AlertTriangle, CheckCircle2,
  ArrowUpRight, ArrowDownRight,
} from 'lucide-react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
} from 'recharts';

const TYPE_INFO = {
  REDDITO:  { label: 'Reddito',  icon: TrendingUp,    color: '#16A34A', tw: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-100 dark:bg-emerald-900/30' },
  SPESE:    { label: 'Spese',    icon: TrendingDown,  color: '#DC2626', tw: 'text-red-500',                            bg: 'bg-red-100 dark:bg-red-900/30' },
  RISPARMI: { label: 'Risparmi', icon: PiggyBank,     color: '#2563EB', tw: 'text-blue-500',                           bg: 'bg-blue-100 dark:bg-blue-900/30' },
  DEBITI:   { label: 'Debiti',   icon: CreditCard,    color: '#EA580C', tw: 'text-orange-500',                         bg: 'bg-orange-100 dark:bg-orange-900/30' },
};

const TONE_META = {
  good: { color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-900/20', icon: CheckCircle2 },
  ok:   { color: 'text-sky-600 dark:text-sky-400',         bg: 'bg-sky-50 dark:bg-sky-900/20',         icon: CheckCircle2 },
  warn: { color: 'text-amber-600 dark:text-amber-400',     bg: 'bg-amber-50 dark:bg-amber-900/20',     icon: AlertTriangle },
  bad:  { color: 'text-red-600 dark:text-red-400',         bg: 'bg-red-50 dark:bg-red-900/20',         icon: AlertTriangle },
};

const scoreColor = (score) => {
  if (score >= 85) return { stroke: '#16A34A', text: 'text-emerald-600 dark:text-emerald-400' };
  if (score >= 70) return { stroke: '#0EA5E9', text: 'text-sky-600 dark:text-sky-400' };
  if (score >= 50) return { stroke: '#F59E0B', text: 'text-amber-600 dark:text-amber-400' };
  return { stroke: '#DC2626', text: 'text-red-600 dark:text-red-400' };
};

const ScoreRing = ({ score }) => {
  const c = scoreColor(score);
  const radius = 60;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  return (
    <div className="relative w-40 h-40">
      <svg className="w-full h-full -rotate-90" viewBox="0 0 140 140">
        <circle cx="70" cy="70" r={radius} stroke="currentColor" strokeWidth="10" fill="none" className="text-muted/40" />
        <circle
          cx="70" cy="70" r={radius}
          stroke={c.stroke} strokeWidth="10" fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.8s ease-out' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-4xl font-extrabold tabular-nums ${c.text}`}>{score}</span>
        <span className="text-xs text-muted-foreground uppercase tracking-wide">/ 100</span>
      </div>
    </div>
  );
};

const MonthlyDetailPage = () => {
  const { year: yearParam, month: monthParam } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const month = parseInt(monthParam, 10);
  const year = parseInt(yearParam, 10);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getMonthlyDetail(month, year);
      setData(res.data);
    } catch (e) {
      toast.error('Errore caricamento dettaglio');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, year]);

  const navMonth = (d) => {
    let m = month + d, y = year;
    if (m < 1) { m = 12; y--; } else if (m > 12) { m = 1; y++; }
    navigate(`/monthly/${y}/${m}`);
  };

  const handleExportPdf = async () => {
    try {
      const blob = await downloadPdfReport('monthly', month, year);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report-${year}-${String(month).padStart(2, '0')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Report PDF pronto');
    } catch {
      toast.error('Errore export PDF');
    }
  };

  if (loading || !data) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-muted rounded w-64" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => <div key={i} className="h-48 bg-muted rounded-xl" />)}
        </div>
      </div>
    );
  }

  const compositionPie = ['SPESE', 'RISPARMI', 'DEBITI']
    .filter((t) => data.totals[t] > 0)
    .map((t) => ({ name: TYPE_INFO[t].label, value: data.totals[t], color: TYPE_INFO[t].color }));

  // Group breakdown by type
  const breakdownByType = data.breakdown_by_category.reduce((acc, b) => {
    if (!acc[b.type]) acc[b.type] = [];
    acc[b.type].push(b);
    return acc;
  }, {});

  const Delta = ({ delta, invert }) => {
    if (delta === null || delta === undefined) return <span className="text-xs text-muted-foreground">—</span>;
    const isPositive = delta > 0;
    const isGood = invert ? !isPositive : isPositive;
    const Icon = isPositive ? ArrowUpRight : ArrowDownRight;
    return (
      <span className={`inline-flex items-center text-xs tabular-nums ${isGood ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`}>
        <Icon className="w-3 h-3" />{Math.abs(delta).toFixed(1)}%
      </span>
    );
  };

  return (
    <div className="space-y-6 animate-in" data-testid="monthly-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/annual')} className="mb-2 -ml-3">
            <ArrowLeft className="w-4 h-4 mr-1" /> Report annuale
          </Button>
          <h1 className="text-3xl font-bold tracking-tight">{MONTHS[month - 1]} {year}</h1>
          <p className="text-muted-foreground">Dettaglio mensile · {data.transaction_count} transazioni</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => navMonth(-1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="icon" onClick={() => navMonth(1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="outline" onClick={handleExportPdf}>
            <FileText className="w-4 h-4 mr-2" /> PDF
          </Button>
        </div>
      </div>

      {/* Score + percentuali — main hero */}
      <Card className="overflow-hidden">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-6 items-center">
            <div className="flex flex-col items-center text-center">
              <ScoreRing score={data.health.score} />
              <Badge className={`mt-3 ${scoreColor(data.health.score).text} bg-transparent border ${scoreColor(data.health.score).text.replace('text-', 'border-')}`}>
                <Award className="w-3 h-3 mr-1" /> Gestione {data.health.label}
              </Badge>
            </div>

            <div className="space-y-3">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" /> Come sta andando il mese
              </h2>
              {data.health.insights.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {data.health.insights.map((i, idx) => {
                    const tone = TONE_META[i.tone] || TONE_META.ok;
                    const ToneIcon = tone.icon;
                    return (
                      <div key={idx} className={`rounded-lg ${tone.bg} p-3 flex items-start gap-2`}>
                        <ToneIcon className={`w-4 h-4 mt-0.5 shrink-0 ${tone.color}`} />
                        <div className="text-sm">
                          <span className="font-semibold">{i.label}: </span>
                          <span className={tone.color}>{i.value}</span>
                          <p className="text-xs text-muted-foreground mt-0.5">{i.detail}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Aggiungi qualche transazione per ottenere insights.</p>
              )}
              {data.health.suggestions.length > 0 && (
                <div className="rounded-lg border border-primary/20 bg-primary/5 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-primary mb-1">Suggerimenti</p>
                  <ul className="text-sm space-y-1">
                    {data.health.suggestions.map((s, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-primary mt-0.5">›</span>
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Composition: % entrate vs uscite */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Type breakdown bars */}
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="text-lg">Composizione del mese</CardTitle></CardHeader>
          <CardContent className="space-y-5">
            {/* Reddito */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-emerald-600" strokeWidth={2} />
                  <span className="font-medium">Reddito</span>
                </div>
                <span className="tabular-nums font-semibold text-emerald-600 dark:text-emerald-400">
                  {formatCurrency(data.totals.REDDITO)}
                </span>
              </div>
              <div className="h-3 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500" style={{ width: '100%' }} />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Base di calcolo per le percentuali sotto
              </p>
            </div>

            {['SPESE', 'RISPARMI', 'DEBITI'].map((t) => {
              const info = data.type_composition[t];
              const meta = TYPE_INFO[t];
              const Icon = meta.icon;
              const pct = info.pct_of_income || 0;
              return (
                <div key={t}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <Icon className={`w-4 h-4 ${meta.tw}`} strokeWidth={2} />
                      <span className="font-medium">{meta.label}</span>
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {pct.toFixed(1)}% del reddito
                      </span>
                    </div>
                    <span className={`tabular-nums font-semibold ${meta.tw}`}>
                      {formatCurrency(info.amount)}
                    </span>
                  </div>
                  <div className="h-3 bg-muted rounded-full overflow-hidden relative">
                    <div
                      className="h-full transition-all duration-700 ease-out"
                      style={{ background: meta.color, width: `${Math.min(pct, 100)}%` }}
                    />
                    {pct > 100 && (
                      <div className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-white">
                        ECCESSO
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Saldo finale */}
            <div className="border-t pt-4 flex items-center justify-between">
              <span className="font-semibold">Saldo netto</span>
              <span className={`text-2xl font-extrabold tabular-nums ${data.balance >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`}>
                {data.balance >= 0 ? '+' : ''}{formatCurrency(data.balance)}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Pie distribuzione uscite */}
        <Card>
          <CardHeader><CardTitle className="text-lg">Distribuzione uscite</CardTitle></CardHeader>
          <CardContent>
            {compositionPie.length > 0 ? (
              <>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={compositionPie} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={2} dataKey="value">
                        {compositionPie.map((e, i) => <Cell key={i} fill={e.color} />)}
                      </Pie>
                      <Tooltip
                        formatter={(v) => formatCurrency(v)}
                        contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-1 mt-2">
                  {compositionPie.map((p, i) => {
                    const total = compositionPie.reduce((s, x) => s + x.value, 0);
                    const pct = (p.value / total) * 100;
                    return (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ background: p.color }} />
                          {p.name}
                        </span>
                        <span className="tabular-nums text-muted-foreground">{pct.toFixed(1)}%</span>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground py-12 text-center">Nessuna uscita registrata.</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top 3 spese */}
      {data.top_expenses.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-lg">Top 3 spese</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.top_expenses.map((c, i) => (
                <div key={c.category_id} className="flex items-center gap-4">
                  <div className="w-8 h-8 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center font-bold text-red-700 dark:text-red-300">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <p className="font-medium truncate">{c.name}</p>
                      <span className="tabular-nums font-semibold ml-2">{formatCurrency(c.amount)}</span>
                    </div>
                    <Progress value={c.pct_in_type} className="h-2" />
                    <div className="flex items-center justify-between mt-0.5">
                      <span className="text-[11px] text-muted-foreground tabular-nums">
                        {c.pct_in_type}% delle spese · {c.count} transazioni
                      </span>
                      <Delta delta={c.delta_pct_vs_prev} invert />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Breakdown completo per categoria, raggruppato per tipo */}
      {Object.keys(breakdownByType).length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-lg">Dettaglio per categoria</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              {['REDDITO', 'SPESE', 'RISPARMI', 'DEBITI'].filter((t) => breakdownByType[t]?.length).map((t) => {
                const meta = TYPE_INFO[t];
                const Icon = meta.icon;
                return (
                  <div key={t}>
                    <h3 className={`text-sm font-semibold uppercase tracking-wider mb-2 flex items-center gap-2 ${meta.tw}`}>
                      <Icon className="w-4 h-4" /> {meta.label}
                    </h3>
                    <div className="space-y-2">
                      {breakdownByType[t].map((c) => (
                        <div key={c.category_id} className="flex items-center justify-between py-1.5 border-b last:border-0">
                          <div className="min-w-0 flex-1">
                            <p className="font-medium truncate">{c.name}</p>
                            <p className="text-[11px] text-muted-foreground tabular-nums">
                              {c.count} mov · {c.pct_in_type}% del totale
                            </p>
                          </div>
                          <div className="text-right ml-3 shrink-0">
                            <p className={`font-semibold tabular-nums ${meta.tw}`}>
                              {formatCurrency(c.amount)}
                            </p>
                            <Delta delta={c.delta_pct_vs_prev} invert={t === 'SPESE' || t === 'DEBITI'} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MonthlyDetailPage;
